import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { finalize } from 'rxjs/operators';
import { PefAutofillEvent, computeAutofillUpdates } from './pef-autofill';
import { PefCellValueChange } from './pef-cell/pef-cell';
import {
  PEF_ADAPTER,
  PefApiData,
  PefData,
  PefSchema,
  flattenPefApiData,
  structurePefApiData,
} from './pef-schema.types';
import { PefSettingsComponent } from './pef-settings/pef-settings';
import { PefSheetComponent } from './pef-sheet/pef-sheet';

@Component({
  selector: 'lib-pef-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    ProgressBarModule,
    MessageModule,
    PefSheetComponent,
    PefSettingsComponent,
  ],
  templateUrl: './pef-editor.html',
  styleUrl: './pef-editor.css',
})
export class PefEditor {
  readonly schema = input.required<PefSchema>();
  /**
   * Show the workbook without letting anyone type in it: input cells render
   * as text, the autofill helpers and "Calcola" are gone. The initial load
   * (and its calculation) still runs, so the sheets show real figures.
   */
  readonly readonly = input<boolean>(false);

  private readonly adapter = inject(PEF_ADAPTER);
  private readonly destroyRef = inject(DestroyRef);

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  /** Full dataset from the last load() or calculate() response */
  protected readonly serverData = signal<PefData>({});
  /** Previously saved user inputs, loaded on init — not considered dirty */
  protected readonly savedUserInputs = signal<PefData>({});
  /** User edits made in this session and not yet folded into savedUserInputs */
  protected readonly userInputs = signal<PefData>({});
  /**
   * Inputs as of the last load()/calculate() — the baseline for "needs
   * recalculation". Deliberately independent from the save bookkeeping in
   * {@link clearDirty}: saving a draft persists the inputs, it does not
   * recalculate them, so it must not make the editor look up to date.
   */
  private readonly lastCalculatedInputs = signal<PefData>({});
  protected readonly isLoading = signal<boolean>(false);
  readonly activeSheet = model<number>(0);
  protected readonly loadError = signal<string | null>(null);

  /**
   * Emitted whenever the user changes a value, with the full input set
   * (saved + current edits). Hosts persist this, so a draft saved without
   * pressing "Calcola" still stores what is on screen.
   */
  readonly inputsChange = output<PefApiData>();

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  /** Every user-supplied value currently in the editor, edited or not */
  protected readonly allInputs = computed<PefData>(() => ({
    ...this.savedUserInputs(),
    ...this.userInputs(),
  }));

  /**
   * Merges server data → saved inputs → session edits so the user sees all
   * values immediately.
   */
  protected readonly mergedData = computed<PefData>(() => ({
    ...this.serverData(),
    ...this.allInputs(),
  }));

  /** Set of valueKeys whose value differs from the last calculated snapshot */
  protected readonly dirtyKeys = computed(() => {
    const calculated = this.lastCalculatedInputs();
    const current = this.allInputs();
    return new Set(
      Object.keys(current).filter((key) => current[key] !== calculated[key]),
    );
  });

  protected readonly hasPendingInputs = computed(
    () => this.dirtyKeys().size > 0,
  );

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  constructor() {
    this.adapter
      .load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ data, savedUserInputs }) => {
          this.serverData.set(flattenPefApiData(data));
          if (savedUserInputs) {
            const flattened = flattenPefApiData(savedUserInputs);
            this.savedUserInputs.set(flattened);
            // serverData was computed from these inputs, so nothing is stale yet.
            this.lastCalculatedInputs.set(flattened);
          }
        },
        error: () => this.loadError.set('Impossibile caricare i dati del PEF.'),
      });
  }

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  protected onCellValueChange(event: PefCellValueChange): void {
    this.userInputs.update((current) => ({
      ...current,
      [event.valueKey]: event.value,
    }));
    this.emitInputs();
  }

  /** Applies several cell edits at once (e.g. an Excel row paste). */
  protected onCellValuesChange(events: PefCellValueChange[]): void {
    if (events.length === 0) return;
    this.userInputs.update((current) => {
      const next = { ...current };
      for (const e of events) next[e.valueKey] = e.value;
      return next;
    });
    this.emitInputs();
  }

  protected currentSheet = computed(
    () => this.schema().sheets[this.activeSheet()] ?? this.schema().sheets[0],
  );

  protected onAutofill(event: PefAutofillEvent): void {
    const sheet = this.schema().sheets.find((s) => s.id === event.sheetId);
    if (!sheet) return;

    const updates = computeAutofillUpdates(event, sheet, this.mergedData());
    if (Object.keys(updates).length > 0) {
      this.userInputs.update((current) => ({ ...current, ...updates }));
      this.emitInputs();
    }
  }

  /**
   * Folds the session edits into the saved set, after the host has persisted
   * them. Values the user has not recalculated stay dirty on purpose — saving
   * is not calculating.
   */
  clearDirty(): void {
    this.savedUserInputs.update((s) => ({ ...s, ...this.userInputs() }));
    this.userInputs.set({});
  }

  /** Everything the user has typed, in the shape the API expects. */
  currentInputs(): PefApiData {
    return structurePefApiData(this.allInputs());
  }

  protected onCalculate(): void {
    const snapshot = this.allInputs();
    this.isLoading.set(true);
    this.adapter
      .calculate(structurePefApiData(snapshot))
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.serverData.set(flattenPefApiData(result));
          this.lastCalculatedInputs.set(snapshot);
        },
        error: () => this.isLoading.set(false),
      });
  }

  private emitInputs(): void {
    this.inputsChange.emit(this.currentInputs());
  }
}
